
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Row, Col,InputGroup, Form } from 'react-bootstrap';
import { 
  FaSearch, 
  FaFilter, 
  FaSortAmountDown, 
  FaSortAmountUp,
  FaDownload,
  FaExchangeAlt,
  FaChartBar,
  FaTimes
} from 'react-icons/fa';
import TransactionTable from './TransactionTable';
import TransactionFilters from './TransactionFilters';
import '../styles/TransactionDetailsModal.css';
import { transactionDetailsService } from '../../service/api/TransfertTitre/transactionDetailsService';
import LoadingSpinner from '../../Validations/ LoadingSpinner';
import EmptyState from '../../SuperAdmin/Domains/EmptyState';
import TransactionSummary from '../../admin/Transactions/transactionQuantite/TransactionSummary';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const TransactionDetailsModal = ({ 
  show, 
  onHide, 
  securityId, 
  securityName,
  domainId = 1 
}) => {
  // États
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filtres
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    transactionType: '',
    clientSearch: '',
    sortBy: 'date',
    sortDesc: true,
    page: 1,
    pageSize: 20
  });
  
  // Données de pagination
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageNumber: 1,
    pageSize: 20,
    totalPages: 1
  });

  // Statistiques
  const [summary, setSummary] = useState(null);

  // Charger les transactions
  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await transactionDetailsService.getTransactions(
        securityId, 
        domainId, 
        filters
      );
      
      if (data.success && data.data) {
        setTransactions(data.data.transactions);
        setPagination({
          totalCount: data.data.totalCount,
          pageNumber: data.data.pageNumber,
          pageSize: data.data.pageSize,
          totalPages: Math.ceil(data.data.totalCount / data.data.pageSize)
        });
        setSummary(data.data.summary);
      } else {
        throw new Error(data.message || 'Erreur de chargement des transactions');
      }
    } catch (err) {
      console.error('Erreur chargement transactions:', err);
      setError(err.message);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [securityId, domainId, filters]);

  // Effet de chargement initial
  useEffect(() => {
    if (show && securityId) {
      loadTransactions();
    }
  }, [show, securityId, loadTransactions]);

  // Gestion de la recherche
  const handleSearch = useCallback((e) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      clientSearch: searchQuery,
      page: 1 // Retour à la première page
    }));
  }, [searchQuery]);

  // Gestion du tri
  const handleSort = useCallback((field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDesc: prev.sortBy === field ? !prev.sortDesc : true
    }));
  }, []);

  // Gestion de la pagination
  const handlePageChange = useCallback((page) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  // Gestion des filtres
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  }, []);

  // Filtrer les transactions localement (pour recherche rapide)
  const filteredTransactions = useMemo(() => {
    if (!searchQuery.trim()) return transactions;
    
    const query = searchQuery.toLowerCase().trim();
    return transactions.filter(transaction => 
      (transaction.sourceClient?.displayName?.toLowerCase().includes(query)) ||
      (transaction.sourceClient?.firstName?.toLowerCase().includes(query)) ||
      (transaction.sourceClient?.lastName?.toLowerCase().includes(query)) ||
      (transaction.destinationClient?.displayName?.toLowerCase().includes(query)) ||
      (transaction.destinationClient?.firstName?.toLowerCase().includes(query)) ||
      (transaction.destinationClient?.lastName?.toLowerCase().includes(query)) ||
      (transaction.transactionReference?.toLowerCase().includes(query))
    );
  }, [transactions, searchQuery]);


  const handleExportPdf = () => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  // ====== TITRE ======
  doc.setFontSize(14);
  doc.text('Détails des Transactions', 14, 15);

  doc.setFontSize(10);
  doc.text(`Titre : ${securityName}`, 14, 22);
  doc.text(
    `Date : ${new Date().toLocaleDateString('fr-FR')}`,
    14,
    28
  );

  // ====== TABLEAU ======
  const tableColumn = [
    'Date',
    'Client Source',
    'Client Destination',
    'Type',
    'Quantité',
    'Valeur',
    'Référence'
  ];

  const tableRows = filteredTransactions.map(tx => ([
    new Date(tx.transactionDate).toLocaleDateString('fr-FR'),
    tx.sourceClient?.displayName 
      || `${tx.sourceClient?.firstName || ''} ${tx.sourceClient?.lastName || ''}`,
    tx.destinationClient?.displayName 
      || `${tx.destinationClient?.firstName || ''} ${tx.destinationClient?.lastName || ''}`,
    tx.transactionType,
    tx.quantity?.toLocaleString('fr-FR'),
    tx.amount?.toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'XAF'
    }),
    tx.transactionReference
  ]));

  autoTable(doc, {
    startY: 35,
    head: [tableColumn],
    body: tableRows,
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    headStyles: {
      fillColor: [13, 110, 253] // Bootstrap primary
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250]
    }
  });

  // ====== TÉLÉCHARGEMENT ======
  doc.save(`transactions_${securityName}.pdf`);
};

  // Rafraîchir les données
  const handleRefresh = () => {
    loadTransactions();
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      centered
      backdrop="static"
      dialogClassName="transaction-modals"
    >
      <Modal.Header closeButton className="bg-light border-bottom-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaExchangeAlt className="text-primary" />
          <div>
            <h5 className="mb-0 fw-bold">Détails des Transactions</h5>
            <small className="text-muted">{securityName}</small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* En-tête avec statistiques */}
        <div className="bg-light border-bottom p-3">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center">
                  <FaChartBar className="text-primary me-2" />
                  <span className="fw-semibold">{pagination.totalCount} transactions</span>
                </div>
                {summary && (
                  <>
                    <div className="vr"></div>
                    <div>
                      <small className="text-muted d-block">Quantité totale</small>
                      <span className="fw-bold">
                        {summary.totalQuantity.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="vr"></div>
                    <div>
                      <small className="text-muted d-block">Valeur totale</small>
                      <span className="fw-bold text-success">
                        {summary.totalValue.toLocaleString('fr-FR', {
                          style: 'currency',
                          currency: 'XAF'
                        })}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
                className="d-inline-flex align-items-center gap-1 filter-button"
              >
                {loading ? 'Chargement...' : 'Rafraîchir'}
              </Button>
            </Col>
          </Row>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="border-bottom p-3 bg-white">
          <Row className="g-2 align-items-center">
            <Col md={6}>
              <Form onSubmit={handleSearch}>
                <InputGroup>
                  <InputGroup.Text className="bg-white border-end-0">
                    <FaSearch className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    placeholder="Rechercher par client, référence..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-start-0"
                  />
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={loading}
                    style={{ marginLeft:'-7px' }}
                  >
                    Rechercher
                  </Button>
                </InputGroup>
              </Form>
            </Col>
            <Col md={6} className="text-end">
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant={showFilters ? "outline-primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="d-flex align-items-center gap-1 filter-button"
                >
                  <FaFilter />
                  Filtres
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleSort('date')}
                  className="d-flex align-items-center gap-1 filter-button"
                >
                  {filters.sortDesc ? <FaSortAmountDown /> : <FaSortAmountUp />}
                  Tri
                </Button>
              </div>
            </Col>
          </Row>

          {/* Filtres avancés */}
          {showFilters && (
            <TransactionFilters
              filters={filters}
              onChange={handleFilterChange}
              onClose={() => setShowFilters(false)}
            />
          )}
        </div>

        {/* Contenu principal */}
        <div className="p-3">
          {loading ? (
            <LoadingSpinner message="Chargement des transactions..." />
          ) : error ? (
            <EmptyState
              icon={FaTimes}
              title="Erreur de chargement"
              message={error}
              action={
                <Button variant="primary" className='filter-button' onClick={handleRefresh}>
                  Réessayer
                </Button>
              }
            />
          ) : transactions.length === 0 ? (
            <EmptyState
              icon={FaExchangeAlt}
              title="Aucune transaction"
              message="Aucune transaction trouvée pour ce titre"
            />
          ) : (
            <>
              {/* Résumé rapide */}
              {summary && (
                <TransactionSummary 
                  summary={summary} 
                  className="mb-4" 
                />
              )}

              {/* Tableau des transactions */}
              <TransactionTable
                transactions={filteredTransactions}
                currentSort={filters.sortBy}
                sortDirection={filters.sortDesc ? 'desc' : 'asc'}
                onSort={handleSort}
              />

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <div className="text-muted small">
                    Affichage de {(filters.page - 1) * filters.pageSize + 1} à{' '}
                    {Math.min(filters.page * filters.pageSize, pagination.totalCount)} sur{' '}
                    {pagination.totalCount} transactions
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page === 1}
                    >
                      
                    </Button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button
                          key={pageNum}
                          variant={filters.page === pageNum ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page === pagination.totalPages}
                    >
                      
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light border-top-0">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <small className="text-muted">
            Données générées le {new Date().toLocaleDateString('fr-FR')}
          </small>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Fermer
            </Button>
            <Button 
              variant="primary" 
              disabled={filteredTransactions.length === 0}
              onClick={handleExportPdf}
            >
              <FaDownload className="me-1" />
              Exporter
            </Button>

          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TransactionDetailsModal;